// Moving shots (the Motion type in src/model/episode.ts): which frame shows when, where each face goes on
// every frame, and turning the magenta face dots found in a frame into faces. Pure; used by the renderer, the editors
// and the server.
import { shotMotion, type Motion, type MotionFrame, type Shot, type ShotFace } from "../model/episode";
import type { Spot } from "../model/face";
import { FRAME_H, FRAME_W, pictureFit } from "./camera";

// Pupil-to-pupil distance over the eye sprite's width, and the 0.8 the dot-made faces are narrowed by (scripts/body.py).
const EYE_SEP = 0.481, NARROW = 0.8;

type Size = { w: number; h: number };
type Pt = { x: number; y: number };

// (dx, dy) turned `deg` degrees clockwise (y points down).
export function turn(dx: number, dy: number, deg: number): Pt {
  const a = (deg * Math.PI) / 180, c = Math.cos(a), s = Math.sin(a);
  return { x: dx * c - dy * s, y: dx * s + dy * c };
}

// The frame showing `t` seconds after the shot starts.
export function motionIndex(m: Motion, t: number): number {
  const n = m.frames.length;
  if (n < 2 || t < 0) return 0;
  const i = Math.floor(t * m.fps);
  return m.loop ? i % n : Math.min(i, n - 1);
}

// How long the animation takes to play through once.
export const motionLength = (m: Motion) => m.frames.length / m.fps;

// Where the three dots go on a face: the two eye centres and the mouth. The inverse of facesFromDots, so a face
// drawn as dots and found again comes back the same.
export function faceDots(f: ShotFace) {
  const sep = (f.eyes.w / NARROW) * EYE_SEP, t = f.tilt ?? 0;
  const at = (p: Pt, dx: number, dy: number) => { const d = turn(dx, dy, t); return { x: p.x + d.x, y: p.y + d.y }; };
  return {
    left: at(f.eyes, -sep / 2, f.eyes.w * 0.04), right: at(f.eyes, sep / 2, f.eyes.w * 0.04), mouth: at(f.mouth, 0, -f.eyes.w * 0.05),
    r: Math.max(4, f.eyes.w * 0.09),
  };
}

// A face moved, sized and tilted so its eyes sit on two eye dots and its mouth on the mouth dot. The brows keep where
// they were placed by hand on `like`, relative to the eyes.
function faceOnDots(like: ShotFace, left: Pt, right: Pt, mouth: Pt): ShotFace {
  const sep = Math.hypot(right.x - left.x, right.y - left.y);
  const w = (sep / EYE_SEP) * NARROW, k = w / like.eyes.w;
  const tilt = (Math.atan2(right.y - left.y, right.x - left.x) * 180) / Math.PI;
  const up = turn(0, -w * 0.04, tilt), down = turn(0, w * 0.05, tilt);
  const x = (left.x + right.x) / 2 + up.x, y = (left.y + right.y) / 2 + up.y;
  const brow = turn((like.brows.x - like.eyes.x) * k, (like.brows.y - like.eyes.y) * k, tilt - (like.tilt ?? 0));
  return {
    character: like.character, eyes: { x, y, w }, ...(Math.abs(tilt) > 0.5 ? { tilt: +tilt.toFixed(1) } : {}),
    brows: { x: x + brow.x, y: y + brow.y, w: like.brows.w * k },
    mouth: { x: mouth.x + down.x, y: mouth.y + down.y, w: like.mouth.w * k },
  };
}

// A face carried from one picture to another of a different size: the same place in the frame.
function carry(f: ShotFace, from: Size, to: Size): ShotFace {
  const a = pictureFit(from), b = pictureFit(to);
  const s = (p: Spot): Spot => ({ x: (a.x + p.x * a.s - b.x) / b.s, y: (a.y + p.y * a.s - b.y) / b.s, w: (p.w * a.s) / b.s });
  return { ...f, eyes: s(f.eyes), brows: s(f.brows), mouth: s(f.mouth) };
}

export type Blob = { x: number; y: number; n: number };

// Faces from the magenta blobs found in each frame, following each character from where they were the frame before
// (starting from the shot's own faces). For each face, the best three nearby blobs laid out like two eyes over a
// mouth are taken; a character with no such three in a frame is left out of that frame.
export function facesFromDots(start: ShotFace[], startSize: Size, frames: (Size & { blobs: Blob[] })[]): ShotFace[][] {
  let prev = start.map(f => ({ face: f, size: startSize }));
  return frames.map(fr => {
    const free = [...fr.blobs];
    const found: ShotFace[] = [];
    prev = prev.map(p => {
      const guess = carry(p.face, p.size, fr), d = faceDots(guess);
      const sep = Math.hypot(d.right.x - d.left.x, d.right.y - d.left.y) || 1;
      const near = free.filter(b => Math.hypot(b.x - guess.eyes.x, b.y - guess.eyes.y) < sep * 4).sort((a, b) => b.n - a.n).slice(0, 7);
      let best: { score: number; l: Blob; r: Blob; m: Blob } | null = null;
      for (const a of near) for (const b of near) for (const m of near) {
        if (a === b || a === m || b === m || a.x >= b.x) continue;
        const s = b.x - a.x, ey = (a.y + b.y) / 2, ex = (a.x + b.x) / 2;
        if (Math.abs(a.y - b.y) > s * 0.8 || m.y - ey < s * 0.3 || m.y - ey > s * 2.5 || Math.abs(m.x - ex) > s) continue;
        const grow = s / sep;
        if (grow < 0.35 || grow > 3.5) continue;
        const score = Math.hypot(ex - (d.left.x + d.right.x) / 2, ey - d.left.y) / sep + Math.abs(Math.log(grow));
        if (!best || score < best.score) best = { score, l: a, r: b, m };
      }
      if (!best) return p;
      for (const b of [best.l, best.r, best.m]) free.splice(free.indexOf(b), 1);
      const face = faceOnDots(guess, best.l, best.r, best.m);
      found.push(face);
      return { face, size: fr };
    });
    return found;
  });
}

// Where every face is on every frame. First the automatic face: frame 0 has the shot's own faces; other frames the
// face found from their dots (if dots are on), moving evenly across frames where none was found, and holding after
// the last. Then the hand-placed faces, which act as keyframes: each says how far off the automatic face was on its
// frame, and that correction blends evenly to the next keyframe's and holds after the last, so fixing a few frames
// fixes the ones around them too. With dots off, this is plain blending between the faces placed by hand.
type Fix = { dx: number; dy: number; k: number };
type Correction = { eyes: Fix; brows: Fix; mouth: Fix; tilt: number };
const PARTS = ["eyes", "brows", "mouth"] as const;
const cache = new WeakMap<Motion, { faces: ShotFace[]; out: ShotFace[][] }>();

// Blends, for each frame, between the frames that have a value: evenly in between, held before the first and after the last.
function fill<T>(n: number, at: (i: number) => T | undefined, mix: (a: T, b: T, t: number, i: number, ia: number, ib: number) => T): (T | undefined)[] {
  const keys = Array.from({ length: n }, (_, i) => i).filter(i => at(i) !== undefined);
  return Array.from({ length: n }, (_, i) => {
    const own = at(i);
    if (own !== undefined) return own;
    const a = [...keys].reverse().find(k => k < i), b = keys.find(k => k > i);
    if (a === undefined && b === undefined) return undefined;
    if (a === undefined) return mix(at(b!)!, at(b!)!, 0, i, b!, b!);
    if (b === undefined) return mix(at(a)!, at(a)!, 0, i, a, a);
    return mix(at(a)!, at(b)!, (i - a) / (b - a), i, a, b);
  });
}

export function motionFaces(shot: Shot, m = shotMotion(shot)): ShotFace[][] {
  if (!m) return [shot.faces];
  const hit = cache.get(m);
  if (hit && hit.faces === shot.faces) return hit.out;
  const frames = m.frames, n = frames.length;
  const inFrame = (f: ShotFace, from: Size) => carry(f, from, { w: FRAME_W, h: FRAME_H }); // compared in the frame
  const per = shot.faces.map(base => {
    const c = base.character;
    // The automatic face, in the frame.
    const auto = fill<ShotFace>(n, i => {
      const f = i === 0 ? base : m.dots ? frames[i]!.found?.find(x => x.character === c) : undefined;
      return f && inFrame(f, i === 0 ? shot.picture ?? frames[0]! : frames[i]!);
    }, (a, b, t) => ({ character: c, eyes: mixSpot(a.eyes, b.eyes, t), brows: mixSpot(a.brows, b.brows, t), mouth: mixSpot(a.mouth, b.mouth, t), tilt: mix(a.tilt ?? 0, b.tilt ?? 0, t) }));
    // The corrections from hand-placed faces; frame 0 is the shot's own face, so it needs none.
    const fixes = fill<Correction>(n, i => {
      if (i === 0) return { eyes: NONE, brows: NONE, mouth: NONE, tilt: 0 };
      const placed = frames[i]!.placed?.find(x => x.character === c);
      if (!placed) return undefined;
      const p = inFrame(placed, frames[i]!), a = auto[i]!;
      return { ...Object.fromEntries(PARTS.map(k => [k, { dx: p[k].x - a[k].x, dy: p[k].y - a[k].y, k: p[k].w / (a[k].w || 1) }])), tilt: (p.tilt ?? 0) - (a.tilt ?? 0) } as Correction;
    }, (a, b, t) => ({ ...Object.fromEntries(PARTS.map(k => [k, { dx: mix(a[k].dx, b[k].dx, t), dy: mix(a[k].dy, b[k].dy, t), k: mix(a[k].k, b[k].k, t) }])), tilt: mix(a.tilt, b.tilt, t) }) as Correction);
    return frames.map((fr, i) => {
      const a = auto[i]!, fx = fixes[i]!;
      const on = Object.fromEntries(PARTS.map(k => [k, { x: a[k].x + fx[k].dx, y: a[k].y + fx[k].dy, w: a[k].w * fx[k].k }])) as Pick<ShotFace, "eyes" | "brows" | "mouth">;
      const tilt = (a.tilt ?? 0) + fx.tilt;
      return carry({ character: c, ...on, ...(Math.abs(tilt) > 0.05 ? { tilt } : {}) }, { w: FRAME_W, h: FRAME_H }, fr);
    });
  });
  const out = frames.map((_, i) => per.map(p => p[i]!));
  cache.set(m, { faces: shot.faces, out });
  return out;
}

const NONE: Fix = { dx: 0, dy: 0, k: 1 };
const mix = (a: number, b: number, t: number) => a + (b - a) * t;
const mixSpot = (p: Spot, q: Spot, t: number): Spot => ({ x: mix(p.x, q.x, t), y: mix(p.y, q.y, t), w: mix(p.w, q.w, t) });

// The frames given a face by hand, for marking keyframes.
export const isKeyframe = (fr: MotionFrame) => !!fr.placed?.length;

// The shot as it looks with `m` playing, for previews of an animation that isn't (yet) the one in the film.
export const withMotion = (shot: Shot, m: Motion): Shot => ({ ...shot, motions: [m], motionId: m.id });

// A new animation's frame list: the shot's picture first, then the made frames.
export const withStill = (shot: Shot, made: MotionFrame[]): MotionFrame[] =>
  shot.picture ? [{ src: shot.picture.src, w: shot.picture.w, h: shot.picture.h }, ...made] : made;
